import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  ChargerStatus,
  EvChargerData,
  EvChargerDetails,
  EvChargerStatusData,
  GenericStatusResponse,
} from 'clipsal-cortex-types/src/api/api-evcharger';

import { RootState } from '../../../app/store';
import { del, get, post } from '../../../common/api/api-helpers';
import { removeSiteDevice } from '../../site/siteSlice';
import availableStatus from './simulated-statuses/charger_status_available.json';
import chargingStatus from './simulated-statuses/charger_status_charging.json';
import finishingStatus from './simulated-statuses/charger_status_completed.json';
import faultedStatus from './simulated-statuses/charger_status_faulted.json';
import preparingStatus from './simulated-statuses/charger_status_preparing.json';
import suspendedEVStatus from './simulated-statuses/charger_status_suspended_ev.json';
import suspendedEVSEStatus from './simulated-statuses/charger_status_suspended_evse.json';
import unavailableStatusCharger from './simulated-statuses/charger_status_unavailable_charger.json';
import unavailableStatusRestart from './simulated-statuses/charger_status_unavailable_restart.json';
import unavailableStatus from './simulated-statuses/charger_status_unavailable.json';
import wifiFaultedStatus from './simulated-statuses/charger_status_wifi_faulted.json';

export type EvChargerState = {
  isLoaded: boolean;
  isUpdating: boolean;
  isDeactivating: boolean;
  hasVisitedRegisterScreen: boolean;
  hasFetchedEvChargerDetails: boolean;
  siteDeviceId: string;
  chargeSimulationType: ChargerStatus | 'None';
} & EvChargerStatusData &
  Pick<EvChargerDetails, 'description' | 'firmware' | 'model' | 'output' | 'sn' | 'charger_id'>;

export const fetchEvChargerDetails = createAsyncThunk<EvChargerDetails, string, { state: RootState }>(
  'evCharger/fetchEvChargerDetails',
  async (siteDeviceId) => {
    return await get<EvChargerDetails>(`/v1/ev_chargers/${siteDeviceId}`);
  }
);

export const fetchEvChargerStatus = createAsyncThunk<EvChargerStatusData, string, { state: RootState }>(
  'evCharger/fetchEvChargerStatus',
  async (siteDeviceId, { getState }) => {
    const state = getState();

    if (state.evCharger.chargeSimulationType !== 'None') {
      const statusToSimulatedResponse: Record<string, EvChargerStatusData> = {
        Available: availableStatus as EvChargerStatusData,
        Charging: chargingStatus as EvChargerStatusData,
        Finishing: finishingStatus as EvChargerStatusData,
        Faulted: faultedStatus as EvChargerStatusData,
        Preparing: preparingStatus as EvChargerStatusData,
        SuspendedEV: suspendedEVStatus as EvChargerStatusData,
        SuspendedEVSE: suspendedEVSEStatus as EvChargerStatusData,
        Unavailable_Disconnect: unavailableStatus as EvChargerStatusData,
        Unavailable_Charger: unavailableStatusCharger as EvChargerStatusData,
        Unavailable_Restart: unavailableStatusRestart as EvChargerStatusData,
        Wifi_Faulted: wifiFaultedStatus as EvChargerStatusData,
      };

      return statusToSimulatedResponse[state.evCharger.chargeSimulationType];
    }

    return await get<EvChargerStatusData>(`/v1/ev_chargers/${siteDeviceId}/status`);
  }
);

export const deactivateEvCharger = createAsyncThunk<GenericStatusResponse, string, { state: RootState }>(
  'evCharger/deactivateEvCharger',
  async (siteDeviceId, { dispatch }) => {
    const response = await del<GenericStatusResponse>(`/v1/ev_chargers/${siteDeviceId}`, {});
    dispatch(removeSiteDevice(Number(siteDeviceId)));
    return response;
  }
);

export const startCharging = createAsyncThunk<GenericStatusResponse, string, { state: RootState }>(
  'evCharger/startCharging',
  async (siteDeviceId) => {
    return await post<GenericStatusResponse>(`/v1/ev_chargers/${siteDeviceId}/start_charge`, {});
  }
);

export const stopCharging = createAsyncThunk<GenericStatusResponse, string, { state: RootState }>(
  'evCharger/stopCharging',
  async (siteDeviceId) => {
    return await post<GenericStatusResponse>(`/v1/ev_chargers/${siteDeviceId}/stop_charge`, {});
  }
);

export const initialState: EvChargerState = {
  isLoaded: false,
  isUpdating: false,
  hasVisitedRegisterScreen: false,
  hasFetchedEvChargerDetails: false,
  isDeactivating: false,
  siteDeviceId: '',
  charger_id: '',
  error: 'NoError',
  error_message: '',
  error_title: '',
  status: 'Unavailable_Disconnect',
  latest_charge_session_start: '',
  latest_charge_session_end: '',
  session_time: 0,
  energy_consumed: 0,
  power: 0,
  timestamp: '',
  description: '',
  firmware: '',
  model: '',
  output: '',
  sn: '',
  chargeSimulationType: 'None',
  clipsal_endpoints: [
    'retrieve',
    'start_charge',
    'pause_charge',
    'resume_charge',
    'stop_charge',
    'get_status',
    'get_charge_current_request',
    'set_charge_current',
    'clear_charge_current',
    'history',
  ],
  charge_current: null,
  charge_current_request: null,
  charge_current_request_max: null,
  battery_level: null,
  is_scheduled_charge_active: null,
  scheduled_charge_start_time: null,
  scheduled_charge_end_time: null,
  estimated_full_charge_time: null,
  timeout_seconds: null,
  control_mode: 'Automatic',
};

export const evChargerSlice = createSlice({
  name: 'evCharger',
  initialState,
  reducers: {
    resetEvChargerData: () => {
      return { ...initialState };
    },
    setIsLoaded: (state, action: PayloadAction<boolean>) => {
      return { ...state, isLoaded: action.payload };
    },
    setSiteDeviceId: (state, action: PayloadAction<string>) => {
      return { ...state, siteDeviceId: action.payload };
    },
    addEnrolledEvChargerDetails: (state, action: PayloadAction<Partial<EvChargerData>>) => {
      return {
        ...state,
        ...action.payload,
        model: action.payload.model ?? state.model,
        sn: action.payload.sn ?? state.sn,
        isLoaded: false,
      };
    },
    setHasVisitedRegisterScreen: (state, action) => {
      return { ...state, hasVisitedRegisterScreen: action.payload };
    },
    setChargeSimulationType: (state, action: PayloadAction<string>) => {
      return { ...state, chargeSimulationType: action.payload as ChargerStatus | 'None' };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvChargerDetails.fulfilled, (state, { payload }) => {
        // We do not require last_status as of now.
        const { last_status: lastStatus, ...chargerDetails } = payload;
        return { ...state, ...chargerDetails, hasFetchedEvChargerDetails: true };
      })
      .addCase(fetchEvChargerDetails.rejected, (state) => {
        return { ...state, hasFetchedEvChargerDetails: true };
      })
      .addCase(fetchEvChargerStatus.fulfilled, (state, action) => {
        return { ...state, ...action.payload, isLoaded: true };
      })
      .addCase(fetchEvChargerStatus.rejected, (state) => {
        return { ...state, isLoaded: true };
      })
      .addCase(deactivateEvCharger.pending, (state) => {
        return { ...state, isDeactivating: true };
      })
      .addCase(deactivateEvCharger.fulfilled, () => {
        return { ...initialState, isDeactivating: false, isLoaded: true };
      })
      .addCase(deactivateEvCharger.rejected, (state) => {
        return { ...state, isDeactivating: false, isLoaded: true };
      })
      .addCase(startCharging.pending, (state) => {
        return { ...state, isUpdating: true };
      })
      .addCase(startCharging.fulfilled, (state) => {
        return { ...state, status: 'Charging', isUpdating: false };
      })
      .addCase(startCharging.rejected, (state) => {
        return { ...state, isUpdating: false };
      })
      .addCase(stopCharging.pending, (state) => {
        return { ...state, isUpdating: true };
      })
      .addCase(stopCharging.fulfilled, (state) => {
        return { ...state, status: 'Finishing', isUpdating: false };
      })
      .addCase(stopCharging.rejected, (state) => {
        return { ...state, isUpdating: false };
      });
  },
});

export const {
  resetEvChargerData,
  setIsLoaded,
  setSiteDeviceId,
  addEnrolledEvChargerDetails,
  setHasVisitedRegisterScreen,
  setChargeSimulationType,
} = evChargerSlice.actions;

export const selectEvChargerData = (state: RootState) => {
  return state.evCharger;
};

export const selectChargeSimulationType = (state: RootState) => {
  return state.evCharger.chargeSimulationType;
};

export const { reducer: evChargerReducer } = evChargerSlice;
